import { html, render } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Dialog } from '@vaadin/vaadin-core'
import { View } from '../view'
import { BookStore } from '../../stores'
import { validateAll } from '../../vaadin'

@customElement('kob-book-edit')
export class BookEdit extends View {

    @property({ type: Object })
    bookStore: BookStore

    @property()
    dialogOpened: boolean

    @property()
    deleteOpened: boolean

    @property()
    deleteYES: string

    formRoot

    render() {
        return html`
            <vaadin-dialog
                theme="kob-modal"
                aria-label="Delete Book"
                .opened="${this.deleteOpened}"
                .renderer="${this.renderDelete}"
            ></vaadin-dialog>
            <vaadin-dialog
                theme="kob-modal"
                aria-label="Edit book"
                .opened="${this.dialogOpened}"
                @opened-changed="${(e: CustomEvent) => (this.dialogOpened = e.detail.value)}"
                .renderer="${this.renderContent}"
            ></vaadin-dialog>
        `
    }

    handleCancel = () => this.dialogOpened = false
    handleDeleteCancel = () => this.deleteOpened = false

    handleSave = async () => {
        validateAll(this.formRoot)
        if (await this.bookStore.save())
            this.dialogOpened = false
    }

    confirmDelete = () => {
        this.deleteYES = null
        this.deleteOpened = true
    }

    handleDelete = async () => {
        await this.bookStore.remove()
        this.deleteOpened = false
        this.dialogOpened = false
    }

    renderContent = (root: HTMLElement, dialog?: Dialog) => {
        this.formRoot = root
        render(
            html`
                <div class="modal">
                    <div class="modal-head">
                        <h3 class="modal-title">${this.bookStore.isNew ? 'Add a book' : 'Edit book'}</h3>
                        <vaadin-button class="modal-close" aria-label="Close" @click="${this.handleCancel}">
                            <vaadin-icon icon="lumo:cross"></vaadin-icon>
                        </vaadin-button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-banner">
                            <vaadin-icon icon="vaadin:warning"></vaadin-icon>
                            <div>
                                Changing the ISBN or title while the book is on your Kobo can create
                                duplicates the next time it syncs.
                            </div>
                        </div>

                        <div class="modal-fields">
                            <vaadin-text-field label="Title"
                                required
                                value=${this.bookStore.title}
                                @input=${(e) => this.bookStore.pending.Title = e.target.value}
                            ></vaadin-text-field>
                            <vaadin-text-field label="Author"
                                value=${this.bookStore.author}
                                @input=${(e) => this.bookStore.pending.Attribution = e.target.value}
                            ></vaadin-text-field>
                            <vaadin-text-field label="ISBN"
                                helper-text="A 13-digit ISBN gets you the right cover and details."
                                value=${this.bookStore.ISBN}
                                @input=${(e) => this.bookStore.pending.ISBN = e.target.value}
                            ></vaadin-text-field>
                        </div>

                        <vaadin-checkbox
                            ?checked=${this.bookStore.completed}
                            @change="${(e) => this.bookStore.setCompleted(e.target.checked)}"
                            label="Mark as completed"
                        ></vaadin-checkbox>
                    </div>

                    <div class="modal-foot">
                        <vaadin-button theme="tertiary error" @click="${this.confirmDelete}">Delete</vaadin-button>
                        <div class="right-group">
                            <vaadin-button theme="tertiary" @click="${this.handleCancel}">Cancel</vaadin-button>
                            <vaadin-button theme="primary" @click="${this.handleSave}">Save</vaadin-button>
                        </div>
                    </div>
                </div>
            `,
            root
        )
    }

    renderDelete = (root: HTMLElement, dialog?: Dialog) => {
        render(
            html`
                <div class="modal">
                    <div class="modal-head">
                        <h3 class="modal-title">Confirm delete</h3>
                        <vaadin-button class="modal-close" aria-label="Close" @click="${this.handleDeleteCancel}">
                            <vaadin-icon icon="lumo:cross"></vaadin-icon>
                        </vaadin-button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-banner danger">
                            <vaadin-icon icon="vaadin:exclamation-circle-o"></vaadin-icon>
                            <div>
                                This permanently deletes the book and everything Koblime knows about it —
                                reading progress, bookmarks and highlights. The file on your Kobo is untouched.
                            </div>
                        </div>

                        <p>You're about to delete <strong>${this.bookStore.title}</strong>.</p>

                        <vaadin-text-field label="Type YES to confirm"
                            required
                            value=${this.deleteYES}
                            @input=${(e) => {
                                this.deleteYES = e.target.value 
                                this.renderDelete(root, dialog)
                            }}
                        ></vaadin-text-field>
                    </div>

                    <div class="modal-foot right">
                        <vaadin-button theme="tertiary" @click="${this.handleDeleteCancel}">Cancel</vaadin-button>
                        <vaadin-button theme="primary error" ?disabled=${this.deleteYES !== 'YES'} @click="${this.handleDelete}">Delete book</vaadin-button>
                    </div>
                </div>
            `,
            root
        )
    }
}
